import { differenceInWeeks } from 'date-fns';

import { isStable } from '@/composables/usePoolHelpers';
import { oneSecondInMs } from '@/composables/useTime';
import { bnum } from '@/lib/utils';
import {
  OnchainPoolData,
  Pool,
  PoolToken,
  RawOnchainPoolData,
} from '@/services/pool/types';
import { TokenInfoMap } from '@/types/TokenList';

import { OnchainDataFormater } from './decorators/onchain-data.formater';
import { AprBreakdown } from '@balancer-labs/sdk';
import { networkId } from '@/composables/useNetwork';
import { getBalancerSDK } from '@/dependencies/balancer-sdk';
import { Pool as SDKPool } from '@balancer-labs/sdk';
import { captureException } from '@sentry/browser';

export default class PoolService {
  constructor(public pool: Pool) {
    this.format();
  }

  /**
   * @summary Statically format various pool attributes.
   */
  public format(): Pool {
    this.pool.isNew = this.isNew;
    this.pool.chainId = networkId.value;
    this.formatPoolTokens();
    return this.pool;
  }

  public get bptPrice(): string {
    return bnum(this.pool.totalLiquidity).div(this.pool.totalShares).toString();
  }

  /**
   * @summary Calculates and sets total liquidity of pool.
   */
  public async setTotalLiquidity(): Promise<string> {
    let totalLiquidity = this.pool.totalLiquidity;

    try {
      const sdkTotalLiquidity = await getBalancerSDK().pools.liquidity(
        this.pool as unknown as SDKPool
      );
      // if totalLiquidity can be computed from coingecko prices, use that
      // else, use the value retrieved from the subgraph
      if (bnum(sdkTotalLiquidity).gt(0)) {
        totalLiquidity = sdkTotalLiquidity;
      }
    } catch (error) {
      captureException(error);
      console.error(`Failed to calc liquidity for: ${this.pool.id}`, error);
    }

    return (this.pool.totalLiquidity = totalLiquidity);
  }

  /**
   * @summary Calculates APRs for pool.
   */
  public async setAPR(): Promise<AprBreakdown> {
    let apr = this.pool.apr;

    try {
      const sdkApr = await getBalancerSDK().pools.apr(this.pool);
      if (sdkApr) apr = sdkApr;
    } catch (error) {
      captureException(error);
      console.error(`Failed to calc APR for: ${this.pool.id}`, error);
    }

    return (this.pool.apr = apr as AprBreakdown);
  }

  formatPoolTokens(): PoolToken[] {
    if (isStable(this.pool.poolType)) return this.pool.tokens;

    return (this.pool.tokens = this.pool.tokens.sort(
      (a, b) => parseFloat(b.weight || '0') - parseFloat(a.weight || '0')
    ));
  }

  public setFeesSnapshot(poolSnapshot: Pool | undefined): string {
    let snapshotFees = '0';
    if (poolSnapshot) snapshotFees = poolSnapshot.totalSwapFee || '0';

    const feesSnapshot = bnum(this.pool.totalSwapFee || 0)
      .minus(snapshotFees)
      .toString();

    return (this.pool.feesSnapshot = feesSnapshot);
  }

  public setVolumeSnapshot(poolSnapshot: Pool | undefined): string {
    let snapshotVolume = '0';
    if (poolSnapshot) snapshotVolume = poolSnapshot.totalSwapVolume || '0';

    const volumeSnapshot = bnum(this.pool.totalSwapVolume || 0)
      .minus(snapshotVolume)
      .toString();

    return (this.pool.volumeSnapshot = volumeSnapshot);
  }

  public setOnchainData(
    rawOnchainData: RawOnchainPoolData,
    tokenMeta: TokenInfoMap
  ): OnchainPoolData | undefined {
    try {
      const onchainData = new OnchainDataFormater(
        this.pool,
        rawOnchainData,
        tokenMeta
      );
      const formatted = (this.pool.onchain = onchainData.format());
      // Fill top-level fields that would normally come from the subgraph
      // (totalShares, swapFee, per-token balance/weight) when they're missing
      // or zero. Lets networks without a subgraph (e.g. Aurora) drive the same
      // UI paths that previously depended on subgraph-populated values.
      this.mirrorOnchainToTopLevel(formatted);
      return formatted;
    } catch (e) {
      console.warn(e);
    }
  }

  private mirrorOnchainToTopLevel(onchain: OnchainPoolData): void {
    if (!onchain) return;

    if (bnum(this.pool.totalShares || '0').eq(0) && onchain.totalSupply) {
      this.pool.totalShares = onchain.totalSupply;
    }

    if (bnum(this.pool.swapFee || '0').eq(0) && onchain.swapFee) {
      this.pool.swapFee = onchain.swapFee;
    }

    if (!onchain.tokens) return;

    // Rebuild pool.tokens / pool.tokensList from the on-chain Vault token
    // order. The Vault stores tokens sorted ascending by address and the
    // exitPool / joinPool calls require the `assets` array to match that
    // exact order — otherwise the Vault reverts with BAL#520 TOKENS_MISMATCH.
    // We can't trust the static seed config to be in Vault order, so always
    // re-derive from on-chain when on-chain data is available. Preserve any
    // metadata we already had (priceRate, sub-pool refs) by merging the
    // existing PoolToken when present.
    const onchainAddresses = Object.keys(onchain.tokens);
    const existingTokens = Array.isArray(this.pool.tokens)
      ? this.pool.tokens
      : [];
    const existingByAddress = new Map(
      existingTokens
        .filter(t => !!t?.address)
        .map(t => [t.address.toLowerCase(), t])
    );

    this.pool.tokens = onchainAddresses.map(addr => {
      const existing = existingByAddress.get(addr);
      const onchainToken = onchain.tokens[addr];
      const merged: PoolToken = {
        ...(existing || {}),
        address: existing?.address || addr,
        balance: onchainToken.balance || existing?.balance || '0',
        decimals: onchainToken.decimals ?? existing?.decimals ?? 18,
        symbol: existing?.symbol || onchainToken.symbol || '',
        weight:
          onchainToken.weight !== undefined
            ? String(onchainToken.weight)
            : existing?.weight ?? null,
        priceRate: existing?.priceRate || '1',
        token: existing?.token || { pool: null, latestUSDPrice: '' },
      } as PoolToken;
      return merged;
    });
    this.pool.tokensList = onchainAddresses;
  }

  public get isNew(): boolean {
    if (!this.pool.createTime) return false;

    return (
      differenceInWeeks(Date.now(), this.pool.createTime * oneSecondInMs) < 1
    );
  }
}
